import React, { useEffect, useMemo, useState } from 'react';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import SidebarLayout from '../../components/Layout/Layout';
import TinyMce from '../../components/GbCpn/TinyMce';
import { useTheme } from '../../components/provider/theme';
import { toast } from 'react-toastify';
import {
    createCategory,
    createPost,
    deleteCategory,
    deletePost,
    getAllCategoriesTrees,
    getPostById,
    updateCategory,
    updatePost,
} from './function/api';
import { CategoryTree, MultiLanguageText, Post } from './function/type';

type LanguageKey = keyof MultiLanguageText;

type HelpPost = {
    id: string;
    rawId: number;
    title: MultiLanguageText;
    content: MultiLanguageText;
    updatedAt: string;
};

type HelpCategoryNode = {
    id: string;
    rawId: number;
    parentId: number;
    type: string;
    order: number;
    active: boolean;
    title: string;
    name: MultiLanguageText;
    updatedAt: string;
    children: HelpCategoryNode[];
    posts: HelpPost[];
};

type EditableCategoryForm = {
    id: number;
    parentId: number;
    type: string;
    order: number;
    active: boolean;
    updatedAt: string;
    name: MultiLanguageText;
};

type EditablePostForm = {
    id: number;
    categoryId: number;
    type: string;
    active: boolean;
    updatedAt: string;
    title: MultiLanguageText;
    excerpt: MultiLanguageText;
    content: MultiLanguageText;
    slug?: string;
};

const emptyMultiLanguageText = (): MultiLanguageText => ({
    vi: '',
    en: '',
});

const getTextValue = (value?: MultiLanguageText) => value?.vi || value?.en || '';

const getLanguageValue = (value: MultiLanguageText | undefined, language: LanguageKey) => value?.[language] || '';

const getDisplayText = (value: MultiLanguageText | undefined, language: LanguageKey) => value?.[language] || getTextValue(value);

const mapTreeNode = (node: CategoryTree): HelpCategoryNode => ({
    id: String(node.id),
    rawId: node.id,
    parentId: node.parent_id,
    type: node.type,
    order: node.order,
    active: node.active,
    title: getTextValue(node.name),
    name: node.name || emptyMultiLanguageText(),
    updatedAt: node.updated_at,
    children: Array.isArray(node.children) ? node.children.map(mapTreeNode) : [],
    posts: Array.isArray(node.posts)
        ? node.posts.map(post => ({
            id: String(post.id),
            rawId: post.id,
            title: post.title || emptyMultiLanguageText(),
            content: post.content || emptyMultiLanguageText(),
            updatedAt: post.updated_at,
        }))
        : [],
});

const toEditableCategoryForm = (category: HelpCategoryNode): EditableCategoryForm => ({
    id: category.rawId,
    parentId: category.parentId,
    type: category.type,
    order: category.order,
    active: category.active,
    updatedAt: category.updatedAt,
    name: category.name,
});

const toEditablePostForm = (post: Post): EditablePostForm => ({
    id: post.id,
    categoryId: post.category_id,
    type: post.type,
    active: post.active,
    updatedAt: post.updated_at,
    title: post.title || emptyMultiLanguageText(),
    excerpt: post.excerpt || emptyMultiLanguageText(),
    content: post.content || emptyMultiLanguageText(),
    slug: post.slug,
});

const findNodeById = (nodes: HelpCategoryNode[], nodeId: string): HelpCategoryNode | undefined => {
    for (const node of nodes) {
        if (node.id === nodeId) {
            return node;
        }

        const childNode = findNodeById(node.children, nodeId);
        if (childNode) {
            return childNode;
        }
    }

    return undefined;
};

const findFirstCategoryId = (nodes: HelpCategoryNode[]): string => nodes[0]?.id || '';

const findCategoryPath = (
    nodes: HelpCategoryNode[],
    nodeId: string,
    parentPath: HelpCategoryNode[] = [],
): HelpCategoryNode[] => {
    for (const node of nodes) {
        const nextPath = [...parentPath, node];

        if (node.id === nodeId) {
            return nextPath;
        }

        const childPath = findCategoryPath(node.children, nodeId, nextPath);
        if (childPath.length) {
            return childPath;
        }
    }

    return [];
};

const formatUpdatedAt = (value?: string) => {
    if (!value) {
        return 'Never updated';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return 'Never updated';
    }

    return date.toLocaleString();
};

const HelpPage: React.FC = () => {
    const { theme } = useTheme();
    const [categories, setCategories] = useState<HelpCategoryNode[]>([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
    const [selectedPostId, setSelectedPostId] = useState<string>('');
    const [editorLanguage, setEditorLanguage] = useState<LanguageKey>('vi');
    const [categoryForm, setCategoryForm] = useState<EditableCategoryForm | null>(null);
    const [postForm, setPostForm] = useState<EditablePostForm | null>(null);
    const [collapsedNodes, setCollapsedNodes] = useState<Record<string, boolean>>({});
    const [isTreeLoading, setIsTreeLoading] = useState<boolean>(true);
    const [isPostLoading, setIsPostLoading] = useState<boolean>(false);
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const [isCreatingCategory, setIsCreatingCategory] = useState<boolean>(false);
    const [isSavingCategory, setIsSavingCategory] = useState<boolean>(false);
    const [isDeletingCategory, setIsDeletingCategory] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const loadCategoryTree = async (nextSelection?: { categoryId?: string; postId?: string }) => {
        setIsTreeLoading(true);

        try {
            const data = await getAllCategoriesTrees();
            setCategories(Array.isArray(data) ? data.map(mapTreeNode) : []);

            if (nextSelection?.categoryId !== undefined) {
                setSelectedCategoryId(nextSelection.categoryId);
            }

            if (nextSelection?.postId !== undefined) {
                setSelectedPostId(nextSelection.postId);
            }
        } catch (error) {
            console.error('Failed to load help category tree:', error);
            toast.error('Failed to load help tree.');
            setCategories([]);
        } finally {
            setIsTreeLoading(false);
        }
    };

    useEffect(() => {
        loadCategoryTree();
    }, []);

    useEffect(() => {
        if (!categories.length) {
            if (selectedCategoryId) {
                setSelectedCategoryId('');
            }

            if (selectedPostId) {
                setSelectedPostId('');
            }

            return;
        }

        const nextCategoryId = findNodeById(categories, selectedCategoryId)
            ? selectedCategoryId
            : findFirstCategoryId(categories);

        if (nextCategoryId !== selectedCategoryId) {
            setSelectedCategoryId(nextCategoryId);
            return;
        }

        const currentCategory = findNodeById(categories, nextCategoryId);
        if (!currentCategory?.posts.length) {
            if (selectedPostId) {
                setSelectedPostId('');
            }

            return;
        }

        const postExists = currentCategory.posts.some(post => post.id === selectedPostId);
        if (!postExists) {
            setSelectedPostId(currentCategory.posts[0].id);
        }
    }, [categories, selectedCategoryId, selectedPostId]);

    const selectedCategory = useMemo(
        () => findNodeById(categories, selectedCategoryId),
        [categories, selectedCategoryId]
    );

    const selectedPost = useMemo(
        () => selectedCategory?.posts.find(post => post.id === selectedPostId),
        [selectedCategory, selectedPostId]
    );

    const selectedCategoryPath = useMemo(
        () => findCategoryPath(categories, selectedCategoryId),
        [categories, selectedCategoryId]
    );

    const canCreatePost = Boolean(selectedCategory && selectedCategory.children.length === 0);
    const canCreateCategory = Boolean(selectedCategory);

    useEffect(() => {
        if (!selectedCategory) {
            setCategoryForm(null);
            return;
        }

        setCategoryForm(toEditableCategoryForm(selectedCategory));
    }, [selectedCategory]);

    useEffect(() => {
        let isMounted = true;

        if (!selectedPost?.rawId) {
            setPostForm(null);
            setIsPostLoading(false);
            return () => {
                isMounted = false;
            };
        }

        setIsPostLoading(true);

        getPostById(selectedPost.rawId)
            .then(post => {
                if (!isMounted) {
                    return;
                }

                setPostForm(toEditablePostForm(post));
            })
            .catch(error => {
                if (!isMounted) {
                    return;
                }

                console.error('Failed to load post detail:', error);
                toast.error('Failed to load post detail.');
                setPostForm(null);
            })
            .finally(() => {
                if (isMounted) {
                    setIsPostLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [selectedPost?.rawId]);

    const cardClassName = theme === 'dark'
        ? 'bg-gray-800 border-gray-700 text-white'
        : 'bg-white border-gray-200 text-gray-900';

    const inputClassName = theme === 'dark'
        ? 'bg-gray-900 border-gray-700 text-white placeholder:text-gray-500'
        : 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400';

    const treeRowClassName = theme === 'dark'
        ? 'border-transparent text-white hover:bg-gray-800'
        : 'border-transparent text-gray-900 hover:bg-gray-100';

    const activeTreeRowClassName = theme === 'dark'
        ? 'border-blue-500 bg-blue-500/10 text-white'
        : 'border-blue-500 bg-blue-50 text-gray-900';

    const treeLineClassName = theme === 'dark' ? 'border-gray-700' : 'border-gray-300';

    const isNodeExpanded = (nodeId: string) => collapsedNodes[nodeId] !== true;

    const expandNode = (nodeId: string) => {
        setCollapsedNodes(prev => {
            if (prev[nodeId] !== true) {
                return prev;
            }

            return {
                ...prev,
                [nodeId]: false,
            };
        });
    };

    const toggleNode = (nodeId: string) => {
        setCollapsedNodes(prev => ({
            ...prev,
            [nodeId]: prev[nodeId] !== true,
        }));
    };

    const updatePostForm = (updater: (prev: EditablePostForm) => EditablePostForm) => {
        setPostForm(prev => (prev ? updater(prev) : prev));
    };

    const updateCategoryForm = (updater: (prev: EditableCategoryForm) => EditableCategoryForm) => {
        setCategoryForm(prev => (prev ? updater(prev) : prev));
    };

    const updatePrimaryMultiTextField = (
        field: 'title' | 'excerpt' | 'content',
        value: string,
    ) => {
        updatePostForm(prev => ({
            ...prev,
            [field]: {
                ...prev[field],
                [editorLanguage]: value,
            },
        }));
    };

    const updateCategoryNameField = (value: string) => {
        updateCategoryForm(prev => ({
            ...prev,
            name: {
                ...prev.name,
                [editorLanguage]: value,
            },
        }));
    };

    const handleCreateRootCategory = async () => {
        setIsCreatingCategory(true);
        try {
            const createdCategory = await createCategory({
                name: {
                    vi: 'New root category',
                    en: '',
                },
                type: 'help',
                order: categories.length + 1,
                active: true,
            });

            toast.success('Root category created.');
            await loadCategoryTree({
                categoryId: String(createdCategory.id),
                postId: '',
            });
        } catch (error) {
            console.error('Failed to create root category:', error);
            toast.error('Failed to create root category.');
        } finally {
            setIsCreatingCategory(false);
        }
    };

    const handleCreateCategory = async () => {
        if (!selectedCategory || !canCreateCategory) {
            return;
        }

        setIsCreatingCategory(true);

        try {
            const createdCategory = await createCategory({
                parent_id: selectedCategory.rawId,
                name: {
                    vi: 'New category',
                    en: '',
                },
                type: selectedCategory.type,
                order: selectedCategory.children.length + 1,
                active: true,
            });

            toast.success('Category created.');
            await loadCategoryTree({
                categoryId: String(createdCategory.id),
                postId: '',
            });
        } catch (error) {
            console.error('Failed to create category:', error);
            toast.error('Failed to create category.');
        } finally {
            setIsCreatingCategory(false);
        }
    };

    const handleSaveCategory = async () => {
        if (!categoryForm) {
            return;
        }

        setIsSavingCategory(true);

        try {
            await updateCategory(categoryForm.id, {
                parent_id: categoryForm.parentId,
                name: categoryForm.name,
                type: categoryForm.type,
                order: categoryForm.order,
                active: categoryForm.active,
            });

            toast.success('Category saved.');
            await loadCategoryTree({
                categoryId: String(categoryForm.id),
                postId: selectedPostId,
            });
        } catch (error) {
            console.error('Failed to save category:', error);
            toast.error('Failed to save category.');
        } finally {
            setIsSavingCategory(false);
        }
    };

    const handleDeleteCategory = async () => {
        if (!categoryForm) {
            return;
        }

        const confirmed = window.confirm(`Delete category "${getTextValue(categoryForm.name) || 'Untitled category'}"?`);
        if (!confirmed) {
            return;
        }

        setIsDeletingCategory(true);

        try {
            await deleteCategory(categoryForm.id);
            setCategoryForm(null);
            setPostForm(null);
            toast.success('Category deleted.');
            await loadCategoryTree({
                categoryId: categoryForm.parentId ? String(categoryForm.parentId) : '',
                postId: '',
            });
        } catch (error) {
            console.error('Failed to delete category:', error);
            toast.error('Failed to delete category.');
        } finally {
            setIsDeletingCategory(false);
        }
    };

    const handleCreatePost = async () => {
        if (!selectedCategory || !canCreatePost) {
            return;
        }

        setIsCreating(true);

        try {
            const createdPost = await createPost({
                category_id: selectedCategory.rawId,
                type: selectedCategory.type,
                active: true,
                title: {
                    vi: 'New post',
                    en: '',
                },
                slug: '',
                excerpt: emptyMultiLanguageText(),
                content: emptyMultiLanguageText(),
                related_ids: [],
            });

            toast.success('Post created.');
            await loadCategoryTree({
                categoryId: selectedCategory.id,
                postId: String(createdPost.id),
            });
        } catch (error) {
            console.error('Failed to create post:', error);
            toast.error('Failed to create post.');
        } finally {
            setIsCreating(false);
        }
    };

    const handleSavePost = async () => {
        if (!postForm) {
            return;
        }

        setIsSaving(true);

        try {
            const updatedPost = await updatePost(postForm.id, {
                category_id: postForm.categoryId,
                type: postForm.type,
                active: postForm.active,
                title: postForm.title,
                excerpt: postForm.excerpt,
                content: postForm.content,
                slug: postForm.slug || '',
                related_ids: [],
            });

            setPostForm(toEditablePostForm(updatedPost));
            toast.success('Post saved.');

            await loadCategoryTree({
                categoryId: selectedCategoryId,
                postId: String(updatedPost.id),
            });
        } catch (error) {
            console.error('Failed to save post:', error);
            toast.error('Failed to save post.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeletePost = async () => {
        if (!postForm || !selectedCategory) {
            return;
        }

        const confirmed = window.confirm(`Delete post "${getTextValue(postForm.title) || 'Untitled post'}"?`);
        if (!confirmed) {
            return;
        }

        setIsDeleting(true);

        try {
            await deletePost(postForm.id);
            setPostForm(null);
            toast.success('Post deleted.');
            await loadCategoryTree({
                categoryId: selectedCategory.id,
                postId: '',
            });
        } catch (error) {
            console.error('Failed to delete post:', error);
            toast.error('Failed to delete post.');
        } finally {
            setIsDeleting(false);
        }
    };

    const selectedCategoryMenuItems: MenuProps['items'] = [
        {
            key: 'add-category',
            label: isCreatingCategory ? 'Creating...' : 'Add Category',
            disabled: !canCreateCategory || isCreatingCategory,
            onClick: handleCreateCategory,
        },
        {
            key: 'add-post',
            label: isCreating ? 'Creating...' : 'Add Post',
            disabled: !canCreatePost || isCreating,
            onClick: handleCreatePost,
        },
    ];

    const renderTreeNode = (node: HelpCategoryNode, depth = 0): React.ReactNode => {
        const nodeExpanded = isNodeExpanded(node.id);
        const isSelectedCategory = node.id === selectedCategoryId;
        const hasChildren = node.children.length > 0;
        const hasPosts = node.posts.length > 0;

        return (
            <div key={node.id} className={depth > 0 ? 'relative' : 'rounded-lg'}>
                {depth > 0 && <div className={`absolute -left-3.25 top-4 w-3 border-t ${treeLineClassName}`} />}
                <div className={`flex items-center gap-2 rounded-lg border px-2 py-2 ${isSelectedCategory ? activeTreeRowClassName : treeRowClassName}`}>
                    <button
                        type="button"
                        onClick={() => toggleNode(node.id)}
                        className="flex h-7 w-7 items-center justify-center rounded text-xs font-semibold"
                        disabled={!hasChildren && !hasPosts}
                    >
                        {hasChildren || hasPosts ? (nodeExpanded ? 'v' : '>') : '-'}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            expandNode(node.id);
                            setSelectedCategoryId(node.id);
                        }}
                        className="min-w-0 flex-1 text-left font-medium"
                    >
                        {getDisplayText(node.name, editorLanguage) || 'Untitled category'}
                    </button>
                    {isSelectedCategory && (
                        <Dropdown
                            trigger={['click']}
                            placement="bottomRight"
                            menu={{ items: selectedCategoryMenuItems }}
                        >
                            <button
                                type="button"
                                onClick={(event) => event.stopPropagation()}
                                className={`rounded-lg px-2 py-1 text-xs font-semibold transition ${theme === 'dark'
                                    ? 'bg-gray-700 text-white hover:bg-gray-600'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                            >
                                +
                            </button>
                        </Dropdown>
                    )}
                </div>

                {nodeExpanded && (hasChildren || hasPosts) && (
                    <div className={`ml-3 mt-2 border-l pl-3 ${treeLineClassName}`}>
                        {hasChildren && (
                            <div className="space-y-2">
                                {node.children.map(childNode => renderTreeNode(childNode, depth + 1))}
                            </div>
                        )}

                        {hasPosts && (
                            <div className={`${hasChildren ? 'mt-2' : ''} space-y-2`}>
                                {node.posts.map(post => {
                                    const isSelectedPost = node.id === selectedCategoryId && post.id === selectedPostId;

                                    return (
                                        <div key={post.id} className="relative">
                                            <div className={`absolute -left-3.25 top-4 w-3 border-t ${treeLineClassName}`} />
                                            <div className={`flex items-center gap-2 rounded-lg border px-2 py-2 ${isSelectedPost ? activeTreeRowClassName : treeRowClassName}`}>
                                                <div className={`h-px w-4 border-t ${treeLineClassName}`} />
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        expandNode(node.id);
                                                        setSelectedCategoryId(node.id);
                                                        setSelectedPostId(post.id);
                                                    }}
                                                    className="min-w-0 flex-1 text-left text-sm font-medium"
                                                >
                                                    {getDisplayText(post.title, editorLanguage) || 'Untitled post'}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <SidebarLayout
            title={<h4 className={`text-2xl font-bold m-0! py-4 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>Help Content</h4>}
        >
            <div className="flex flex-col gap-6">
                <div className={`overflow-hidden rounded-xl border shadow-sm ${cardClassName}`}>
                    <div className="grid min-h-180 grid-cols-2 xl:grid-cols-[320px_minmax(0,1fr)]">
                        <div className={`border-b p-4 xl:border-b-0 xl:border-r ${theme === 'dark' ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
                            <div className="mb-4 flex items-center justify-between gap-3">
                                <h3 className="text-2xl font-semibold">Tree</h3>
                                <button
                                    type="button"
                                    onClick={() => loadCategoryTree()}
                                    className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white! transition hover:bg-blue-700"
                                >
                                    Reload
                                </button>
                            </div>

                            <div className="space-y-2">
                                {isTreeLoading ? (
                                    <div className={`rounded-lg border border-dashed p-4 text-sm ${theme === 'dark' ? 'border-gray-700 text-gray-400' : 'border-gray-300 text-gray-500'}`}>
                                        Loading help tree...
                                    </div>
                                ) : categories.length ? categories.map(node => renderTreeNode(node)) : (
                                    <div className="flex flex-col gap-3">
                                        <div className={`rounded-lg border border-dashed p-4 text-sm ${theme === 'dark' ? 'border-gray-700 text-gray-400' : 'border-gray-300 text-gray-500'}`}>
                                            No categories found.
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleCreateRootCategory}
                                            disabled={isCreatingCategory}
                                            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white! transition hover:bg-blue-700 disabled:opacity-50"
                                        >
                                            {isCreatingCategory ? 'Creating...' : 'Create First Category'}
                                        </button>
                                    </div>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={handleCreateRootCategory}
                                disabled={isCreatingCategory}
                                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white! transition hover:bg-blue-700 disabled:opacity-50 w-full mt-4!"
                            >
                                {isCreatingCategory ? 'Creating...' : 'Create Category'}
                            </button>
                        </div>

                        <div className="grid min-h-180 grid-rows-[280px_minmax(0,1fr)]">
                            <div className={`border-b p-4 ${theme === 'dark' ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
                                <div className="flex h-full flex-col gap-2">
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="text-sm font-semibold uppercase tracking-wide text-gray-500">Language</div>
                                        <div className="flex items-center gap-2">
                                            {(['vi', 'en'] as LanguageKey[]).map(language => {
                                                const isActiveLanguage = editorLanguage === language;

                                                return (
                                                    <button
                                                        key={language}
                                                        type="button"
                                                        onClick={() => setEditorLanguage(language)}
                                                        className={`rounded-lg px-3 py-1 text-xs font-semibold uppercase transition ${isActiveLanguage
                                                            ? 'bg-blue-600 text-white! hover:bg-blue-700'
                                                            : theme === 'dark'
                                                                ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                                    >
                                                        {language}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="text-xl font-semibold">Category</div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={handleSaveCategory}
                                                disabled={!categoryForm || isSavingCategory}
                                                className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${categoryForm && !isSavingCategory
                                                    ? 'bg-blue-700 text-white! hover:bg-blue-800'
                                                    : theme === 'dark'
                                                        ? 'bg-gray-800 text-gray-500'
                                                        : 'bg-gray-100 text-gray-400'}`}
                                            >
                                                {isSavingCategory ? 'Saving...' : 'Save Category'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleDeleteCategory}
                                                disabled={!categoryForm || isDeletingCategory}
                                                className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${categoryForm && !isDeletingCategory
                                                    ? 'bg-rose-600 text-white! hover:bg-rose-700'
                                                    : theme === 'dark'
                                                        ? 'bg-gray-800 text-gray-500'
                                                        : 'bg-gray-100 text-gray-400'}`}
                                            >
                                                {isDeletingCategory ? 'Deleting...' : 'Delete Category'}
                                            </button>
                                        </div>
                                    </div>
                                    {categoryForm && (
                                        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_120px_140px]">
                                            <input
                                                type="text"
                                                value={getLanguageValue(categoryForm.name, editorLanguage)}
                                                onChange={(event) => updateCategoryNameField(event.target.value)}
                                                placeholder={`Category name (${editorLanguage.toUpperCase()})`}
                                                className={`w-full rounded-lg border px-3 py-2 outline-none ${inputClassName}`}
                                            />
                                            <input
                                                type="number"
                                                value={categoryForm.order}
                                                onChange={(event) => updateCategoryForm(prev => ({
                                                    ...prev,
                                                    order: Number(event.target.value) || 0,
                                                }))}
                                                placeholder="Order"
                                                className={`w-full rounded-lg border px-3 py-2 outline-none ${inputClassName}`}
                                            />
                                            <label className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${inputClassName}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={categoryForm.active}
                                                    onChange={(event) => updateCategoryForm(prev => ({
                                                        ...prev,
                                                        active: event.target.checked,
                                                    }))}
                                                />
                                                Active
                                            </label>
                                        </div>
                                    )}
                                    <div className='border border-dashed w-full border-gray-300' />
                                    <div className="flex items-center justify-between gap-3 mb-4">
                                        <div className="text-2xl font-semibold">Post</div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={handleSavePost}
                                                disabled={!postForm || isPostLoading || isSaving}
                                                className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${postForm && !isPostLoading && !isSaving
                                                    ? 'bg-blue-600 text-white! hover:bg-blue-700'
                                                    : theme === 'dark'
                                                        ? 'bg-gray-800 text-gray-500'
                                                        : 'bg-gray-100 text-gray-400'}`}
                                            >
                                                {isSaving ? 'Saving...' : 'Save'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleDeletePost}
                                                disabled={!postForm || isDeleting}
                                                className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${postForm && !isDeleting
                                                    ? 'bg-red-600 text-white! hover:bg-red-700'
                                                    : theme === 'dark'
                                                        ? 'bg-gray-800 text-gray-500'
                                                        : 'bg-gray-100 text-gray-400'}`}
                                            >
                                                {isDeleting ? 'Deleting...' : 'Delete'}
                                            </button>
                                        </div>
                                    </div>
                                    {selectedPost ? (
                                        <>
                                            <div className={`truncate text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>
                                                {selectedCategoryPath.map(node => getDisplayText(node.name, editorLanguage) || 'Untitled category').join(' / ')} / {getDisplayText(postForm?.title || selectedPost?.title, editorLanguage) || 'Untitled post'}
                                            </div>
                                            <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                                Last updated: {formatUpdatedAt(postForm?.updatedAt || selectedPost.updatedAt)}
                                            </div>
                                        </>
                                    ) : selectedCategory ? (
                                        <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                            Select a post from the last level of {selectedCategory.title || 'this category'}.
                                        </div>
                                    ) : (
                                        <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                            Select a category tree item first.
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className={`${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
                                <div className="min-h-0 p-4">
                                    {isPostLoading ? (
                                        <div className={`flex min-h-130 items-start rounded-lg border border-dashed p-6 text-sm ${theme === 'dark' ? 'border-gray-700 text-gray-400' : 'border-gray-300 text-gray-500'}`}>
                                            Loading post detail...
                                        </div>
                                    ) : postForm ? (
                                        <div className="space-y-4">
                                            <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_180px]">
                                                <div className={`flex flex-col gap-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                                    <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                                        Title
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={getLanguageValue(postForm.title, editorLanguage)}
                                                        onChange={(event) => updatePrimaryMultiTextField('title', event.target.value)}
                                                        placeholder={`Post title (${editorLanguage.toUpperCase()})`}
                                                        className={`w-full rounded-lg border px-3 py-2 outline-none ${inputClassName}`}
                                                    />
                                                </div>
                                                <label className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${inputClassName}`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={postForm.active}
                                                        onChange={(event) => updatePostForm(prev => ({
                                                            ...prev,
                                                            active: event.target.checked,
                                                        }))}
                                                    />
                                                    Active
                                                </label>
                                            </div>
                                             <div className={`flex flex-col gap-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                                <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                                    Slug
                                                </label>
                                                <textarea
                                                    value={postForm.slug || ''}
                                                    onChange={(event) => updatePostForm(prev => ({
                                                        ...prev,
                                                        slug: event.target.value,
                                                    }))}
                                                    placeholder={`Slug (${editorLanguage.toUpperCase()})`}
                                                    rows={3}
                                                    className={`w-full rounded-lg border px-3 py-2 outline-none ${inputClassName}`}
                                                />
                                            </div>
                                            <div className={`flex flex-col gap-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                                <label className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                                    Excerpt
                                                </label>
                                                <textarea
                                                    value={getLanguageValue(postForm.excerpt, editorLanguage)}
                                                    onChange={(event) => updatePrimaryMultiTextField('excerpt', event.target.value)}
                                                    placeholder={`Excerpt (${editorLanguage.toUpperCase()})`}
                                                    rows={3}
                                                    className={`w-full rounded-lg border px-3 py-2 outline-none ${inputClassName}`}
                                                />
                                            </div>

                                            <TinyMce
                                                key={`${postForm.id}-${editorLanguage}`}
                                                option={{
                                                    min_height: 800,
                                                    max_height: 1200,
                                                }}
                                                defaultValue={getLanguageValue(postForm.content, editorLanguage)}
                                                onChange={(content: string) => updatePrimaryMultiTextField('content', content)}
                                            />
                                        </div>
                                    ) : (
                                        <div className={`flex min-h-130 items-start rounded-lg border border-dashed p-6 text-sm ${theme === 'dark' ? 'border-gray-700 text-gray-400' : 'border-gray-300 text-gray-500'}`}>
                                            Select a post to edit content.
                                        </div>
                                    )}
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </SidebarLayout>
    );
};

export default HelpPage;